<template>
  <PageModal
    v-if="!!propositionViewModalId"
    :show-modal="!!propositionViewModalId"
    :page-modal-name="'proposition-page'"
    @close="handleCloseModal"
  >
    <ModalNavigation v-if="propositionViewModalType" slot="nav" />
    <PropositionAndSwapDetails
      :proposition-view-modal-id="propositionViewModalId"
      :proposition-view-modal-id2="propositionViewModalId2"
      :proposition-view-modal-type="propositionViewModalType"
    />
  </PageModal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

// Async: the modal's contents (PropositionAndSwapDetails → Proposition →
// maps) pull in maplibre, so a logged-out visitor's first load never pays for
// them. The container itself is always mounted: its route watchers below must
// be running before a modal opens to know how that modal should close.
const loadPageModal = () =>
  import(
    /* webpackChunkName: "page-modal" */ '@/components/Modals/PageModal/PageModal'
  );
const loadModalNavigation = () =>
  import(
    /* webpackChunkName: "page-modal" */ '@/components/Modals/PageModal/ModalNavigation'
  );
const loadPropositionAndSwapDetails = () =>
  import(
    /* webpackChunkName: "page-modal" */ '@/components/Modals/PageModal/PropositionAndSwapDetails'
  );

let preloadRequested = false;

// A logged-in user is one tap away from a proposition modal. Fetch its chunk
// in the background, so that first tap opens the modal at once instead of
// hiding the tab bar and then waiting for the download.
function preloadModalComponents() {
  if (preloadRequested) return;
  preloadRequested = true;

  const run = () =>
    Promise.all([
      loadPageModal(),
      loadModalNavigation(),
      loadPropositionAndSwapDetails()
    ]).catch(() => {
      preloadRequested = false;
    });
  if (window.requestIdleCallback) {
    window.requestIdleCallback(run, { timeout: 2000 });
  } else {
    setTimeout(run, 500);
  }
}

export default {
  components: {
    PageModal: loadPageModal,
    ModalNavigation: loadModalNavigation,
    PropositionAndSwapDetails: loadPropositionAndSwapDetails
  },

  computed: {
    ...mapGetters({
      appReady: 'app/ready',
      isAuthenticated: 'app/isAuthenticated',
      propositionViewModalId: 'ui/propositionViewModalId',
      propositionViewModalId2: 'ui/propositionViewModalId2',
      propositionViewModalType: 'ui/propositionViewModalType'
    }),

    shouldPreloadModal() {
      return !!(this.appReady && this.isAuthenticated);
    }
  },

  watch: {
    shouldPreloadModal: {
      immediate: true,
      handler(shouldPreload) {
        if (shouldPreload) preloadModalComponents();
      }
    },

    // The ad sets the tab title (vue-head), and when it is destroyed vue-head
    // resets the title to the bare site name. The page underneath was never
    // left, so it does not set its own title again: ask it to once the modal
    // has gone (after this flush, which destroys the ad).
    propositionViewModalId(now, was) {
      if (was && !now) this.$nextTick(this.restorePageTitle);
    },

    // vue-router keys every history entry; only a push creates a new key.
    // A modal arriving on a NEW key was pushed — its arrow can go back over
    // it. Replace, deep link and a duplicated push keep the old key.
    // Declared before the $route watcher so it still sees the previous key.
    '$route.query.propositionPageId'(now, was) {
      const key = window.history.state?.key ?? null;
      if (now && !was)
        this.modalEntryKey = key !== this.lastEntryKey ? key : null;
      if (!now) this.modalEntryKey = null;
      this.closing = false;
      this.lastEntryKey = key;
    },
    $route(to, from) {
      if (from.query.propositionPageId != to.query.propositionPageId) {
        this.setPageModalActivePage('proposition');
      }
      this.closing = false;
      this.lastEntryKey = window.history.state?.key ?? null;
    }
  },

  created() {
    // Non-reactive bookkeeping for the close decision above.
    this.lastEntryKey = window.history.state?.key ?? null;
    this.modalEntryKey = null;
    this.closing = false;
  },

  methods: {
    ...mapActions({
      setPageModalActivePage: 'ui/setPageModalActivePage'
    }),

    restorePageTitle() {
      (this.$route.matched || []).forEach(record => {
        Object.values(record.instances || {}).forEach(page => {
          if (page) page.$emit('updateHead');
        });
      });
    },

    handleCloseModal() {
      if (this.closing) return;
      const onPushedEntry =
        this.modalEntryKey != null &&
        window.history.state?.key === this.modalEntryKey;
      if (onPushedEntry) {
        this.closing = true;
        this.$router.go(-1);
        return;
      }
      const newQuery = { ...this.$route.query };
      delete newQuery.propositionPageId;
      delete newQuery.propositionPageId2;
      delete newQuery.propositionPageSection;
      delete newQuery.type;
      delete newQuery.stripped;
      delete newQuery.hideContactCta;
      return this.$router.replace({ query: newQuery });
    }
  }
};
</script>

<style lang="scss" scoped></style>
